import { css, cx } from '@emotion/css';
import { useAccountSelector } from 'src/hooks';
import { MTypography } from 'src/lib';
import { Spacing } from 'src/styles';
import { getAccountFundingInstructions } from 'src/utils';

/** Beats MTypography's margin reset. No top margin, so a bold title stays attached to the text under it. */
const paragraph = css`
  && {
    ${Spacing.mb16}
  }
`;

/** Beats MTypography's margin reset. */
const sectionHeading = css`
  && {
    ${Spacing.mt16}
  }
`;

export const DepositByCheckInstructions = () => {
  const { account } = useAccountSelector();

  return (
    <>
      <MTypography variant='body1' className={paragraph}>
        Paper check deposits received before 1:00pm CT are processed same day. Please refer to the check funding
        instructions below to avoid having your check returned to you as unacceptable.
      </MTypography>

      <MTypography variant='body1' weight='bold' className={sectionHeading}>
        Make check(s) payable to:
      </MTypography>
      <MTypography variant='body1' className={paragraph}>
        Apex Clearing FBO, {getAccountFundingInstructions(account)}
      </MTypography>

      <MTypography variant='body1' weight='bold' className={sectionHeading}>
        Mail checks to:
      </MTypography>
      <MTypography variant='body1' className={paragraph}>
        <span>TradingBlock</span>
        <br />
        <span>Attn. Cashiering</span>
        <br />
        <span>311 S Wacker Dr, Suite 1775</span>
        <br />
        <span>Chicago, IL 60606</span>
        <br />
      </MTypography>

      <MTypography variant='body1' weight='bold' className={sectionHeading}>
        Acceptable Check Deposits
      </MTypography>
      <MTypography variant='body1' className={paragraph}>
        <MTypography variant='body1' weight='bold' inline>
          My IPO does not accept “third party” funding of any kind
        </MTypography>
        . This means that the name on your bank account must match the name on your My IPO account exactly. For example,
        money transfers from a trust or business bank account (owned by you) to an individual or joint My IPO account
        are considered third-party and{' '}
        <MTypography variant='body1' weight='bold' inline>
          will not be accepted.
        </MTypography>
      </MTypography>
      <ul className={cx(Spacing.pl20, Spacing.mt0)}>
        <li>
          <MTypography variant='body1'>Checks must be payable in U.S. dollars and through a U.S. bank.</MTypography>
        </li>
        <li>
          <MTypography variant='body1'>
            Personal checks must be drawn from a bank account in an account owner’s name and must include &quot;Jr&quot;
            or &quot;Sr&quot; if applicable.
          </MTypography>
        </li>
        <li>
          <MTypography variant='body1'>
            Checks from joint checking accounts may be deposited into either checking account owner’s My IPO account.
          </MTypography>
        </li>
        <li>
          <MTypography variant='body1'>
            Checks from an individual checking account may be deposited into a My IPO joint account if that person is
            one of the account owners.
          </MTypography>
        </li>
        <li>
          <MTypography variant='body1'>
            Investment Club checks should be drawn from a checking account in the name of the Investment Club. If a
            member of the Investment Club remits a personal check, the check must be payable to: &quot;Apex Clearing /
            FBO the Investment Club name.&quot; (&quot;FBO&quot; stands for &quot;For the Benefit Of&quot;)
          </MTypography>
        </li>
      </ul>

      <MTypography variant='body1' weight='bold'>
        Unacceptable Check Deposits
      </MTypography>

      <ul className={cx(Spacing.pl20, Spacing.mt0)}>
        <li>
          <MTypography variant='body1'>Cashier’s check</MTypography>
        </li>
        <li>
          <MTypography variant='body1'>Money Orders</MTypography>
        </li>
        <li>
          <MTypography variant='body1'>Foreign instruments</MTypography>
        </li>
        <li>
          <MTypography variant='body1'>Thrift withdrawal orders</MTypography>
        </li>
        <li>
          <MTypography variant='body1'>Domestic drafts</MTypography>
        </li>
        <li>
          <MTypography variant='body1'>
            Checks that have been double-endorsed (checks with more than one signature on the back)
          </MTypography>
        </li>
        <li>
          <MTypography variant='body1'>
            Third-party checks not properly made out and endorsed per the rules stated in the &quot;Acceptable
            Deposits&quot; section above
          </MTypography>
        </li>
        <li>
          <MTypography variant='body1'>Checks from minors</MTypography>
        </li>
        <li>
          <MTypography variant='body1'>Check dated over six months old</MTypography>
        </li>
        <li>
          <MTypography variant='body1'>Travelers checks</MTypography>
        </li>
        <li>
          <MTypography variant='body1'>Credit card checks</MTypography>
        </li>
      </ul>
    </>
  );
};
